import type { Decor, ObjectId, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shadow, shade, type Ctx } from './paint';
import { wind } from './nature';

/** Props for the homes and the night market (phase B). */
type Drawer = (c: Ctx, r: Rect, t: number) => void;

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

function sign(c: Ctx, text: string, cx: number, y: number, w: number, bg: string, fg = '#fff6dc'): void {
  block(c, cx - w / 2, y, w, 11, bg, shade(bg, 0.25), shade(bg, -0.3));
  c.font = `900 8px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = 'rgba(0,0,0,0.35)';
  c.fillText(text, cx + 0.5, y + 6.2);
  c.fillStyle = fg;
  c.fillText(text, cx, y + 5.7);
}

/** A night-market stall: counter, striped canopy, a glowing sign and a bare bulb. */
function stall(c: Ctx, r: Rect, t: number, label: string, canopy: string, goods: (c: Ctx, r: Rect, t: number) => void): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3.5, 0.35);
  // Back posts.
  rect(c, OUTLINE, x + 1, y - 34, 2, 36);
  rect(c, OUTLINE, x + w - 3, y - 34, 2, 36);
  // Canopy with scalloped stripes.
  const top = y - 40;
  for (let i = 0; i < w; i += 6) {
    c.fillStyle = (i / 6) % 2 ? '#fff6e6' : canopy;
    c.fillRect(x - 2 + i, top, Math.min(6, w + 4 - i), 9);
    c.beginPath();
    c.arc(x + 1 + i, top + 9, 3, 0, Math.PI);
    c.fill();
  }
  rect(c, 'rgba(0,0,0,0.25)', x - 2, top + 9, w + 4, 1);
  sign(c, label, x + w / 2, top - 11, Math.max(28, label.length * 9 + 8), '#c0392b');
  // Bulb swinging a little in the breeze.
  const sw = wind.at(x, y) * 1.2 + Math.sin(t * 1.7 + x) * 0.6;
  c.strokeStyle = OUTLINE;
  c.lineWidth = 0.6;
  c.beginPath();
  c.moveTo(x + w / 2, top + 9);
  c.lineTo(x + w / 2 + sw, top + 16);
  c.stroke();
  ellipse(c, '#fff3b0', x + w / 2 + sw, top + 17.5, 1.8, 2);
  ellipse(c, 'rgba(255,230,140,0.25)', x + w / 2 + sw, top + 18, 6, 5);
  // Goods on the counter, then the counter front.
  goods(c, r, t);
  block(c, x, y - 8, w, h + 8, '#9a6a44', '#b88a5c', '#6e4a2e');
  rect(c, '#f2e6cf', x + 1, y - 9, w - 2, 2);
  for (let i = 4; i < w - 2; i += 8) rect(c, 'rgba(0,0,0,0.15)', x + i, y - 6, 0.6, h + 4);
}

function steam(c: Ctx, x: number, y: number, t: number): void {
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.6 + i / 3) % 1;
    ellipse(c, `rgba(255,255,255,${0.35 * (1 - k)})`, x + Math.sin(t * 2 + i * 2) * 2, y - k * 12, 1.5 + k * 2, 1.2 + k * 1.5);
  }
}

export const OBJ3: Partial<Record<ObjectId, Drawer>> = {
  // --- Grandma Lee's house ---------------------------------------------------------
  grCooker(c, r, t) {
    const cx = r.x + r.w / 2, by = r.y - 9;
    shadow(c, cx, by + 1, 6, 1.6);
    // The iconic red rice cooker with chrome handles.
    block(c, cx - 6, by - 10, 12, 10, '#d0453a', '#ec6a5c', '#9c2a22');
    ellipse(c, '#e8685c', cx, by - 10, 6, 2);
    ellipse(c, '#c8cdd2', cx, by - 11.5, 3, 1.3);
    rect(c, '#dfe3e6', cx - 8, by - 7, 2, 2);
    rect(c, '#dfe3e6', cx + 6, by - 7, 2, 2);
    rect(c, '#fff3b0', cx - 1, by - 4, 2, 1.2);
    steam(c, cx, by - 13, t);
  },
  grTea(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 6, w, 6, '#6e3f22', '#8a5530', '#4a2a16');
    rect(c, '#4a2a16', x + 2, y, 2, r.h);
    rect(c, '#4a2a16', x + w - 4, y, 2, r.h);
    // Clay teapot and tiny cups on a tray.
    block(c, x + 4, y - 9, w - 8, 3, '#3a2418');
    ellipse(c, '#8a4a2a', x + w / 2 - 3, y - 11, 3.5, 3);
    rect(c, '#8a4a2a', x + w / 2 + 0.5, y - 12.5, 3, 1.2);
    for (const dx of [3, 7, 11]) ellipse(c, '#f4f1ea', x + w / 2 + dx - 2, y - 8.5, 1.4, 1);
  },
  grPhotos(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 1, 3);
    block(c, x, y - 14, w, r.h + 14, '#7a4a2a', '#9a6440', '#55311a');
    rect(c, '#55311a', x + 2, y - 4, w - 4, 1);
    // Framed photos standing on top, sepia and black-and-white.
    const frames = [['#d8c7a0', 7, 9], ['#b9b2a6', 9, 11], ['#e2d4b0', 6, 8], ['#c8bca4', 8, 7]] as const;
    let fx = x + 2;
    for (const [col, fw, fh] of frames) {
      block(c, fx, y - 14 - fh, fw, fh, '#3a2418');
      rect(c, col, fx + 1, y - 13 - fh, fw - 2, fh - 2);
      ellipse(c, 'rgba(60,40,30,0.5)', fx + fw / 2, y - 14 - fh / 2, 1.3, 1.6);
      fx += fw + 1;
    }
  },
  grSewing(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 7, w, 7, '#6e4a2e', '#8a6040', '#4a2e1a');
    // Cast-iron treadle legs.
    rect(c, '#2b2b30', x + 2, y, 1.5, r.h);
    rect(c, '#2b2b30', x + w - 3.5, y, 1.5, r.h);
    // Black machine with gold lettering.
    block(c, x + 5, y - 15, w - 12, 8, '#1f1f24', '#3a3a44');
    block(c, x + 4, y - 17, 6, 10, '#1f1f24', '#3a3a44');
    rect(c, '#d9b450', x + 11, y - 13, 8, 0.8);
    ellipse(c, '#c8cdd2', x + w - 6, y - 12, 2, 2.5);
  },
  grCalendar(c, r) {
    const cx = r.x + r.w / 2, top = r.y - 30;
    block(c, cx - 7, top, 14, 16, '#c0392b', '#e05a48');
    rect(c, '#fbf8ef', cx - 6, top + 4, 12, 11);
    c.font = `900 7px ${CJK}`;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillStyle = '#c0392b';
    c.fillText(String(new Date().getDate()), cx, top + 10);
    rect(c, 'rgba(0,0,0,0.12)', cx - 6, top + 14, 12, 1);
  },

  // --- Bo's home --------------------------------------------------------------------
  boConsole(c, r, t) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 6, w, r.h + 6, '#5a3a24', '#7a5034', '#3a2416');
    // Flat TV showing a bouncing game screen.
    block(c, x + 3, y - 26, w - 6, 18, '#15161b');
    const sx = x + 5, sy = y - 24, sw = w - 10, sh = 14;
    c.fillStyle = '#3a7ad9';
    c.fillRect(sx, sy, sw, sh);
    rect(c, '#57c278', sx, sy + sh - 4, sw, 4);
    ellipse(c, '#ffd34e', sx + ((t * 20) % sw), sy + 6 - Math.abs(Math.sin(t * 5)) * 4, 1.5, 1.5);
    rect(c, '#e2493b', sx + sw - 8, sy + sh - 8, 4, 4);
    // Console and two controllers.
    block(c, x + 4, y - 5, 10, 3, '#f4f1ea');
    ellipse(c, '#e2493b', x + w - 10, y - 3, 2.5, 1.5);
    ellipse(c, '#3a8fd9', x + w - 4, y - 3, 2.5, 1.5);
  },
  boAquarium(c, r, t) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 6, w, r.h + 6, '#2b3440', '#3a4656');
    // Glass tank with plants, bubbles, fish and a turtle.
    const tx = x + 1, ty = y - 24, tw = w - 2, th = 17;
    block(c, tx, ty, tw, th, '#1b2a36');
    const g = c.createLinearGradient(0, ty, 0, ty + th);
    g.addColorStop(0, 'rgba(120,220,255,0.85)');
    g.addColorStop(1, 'rgba(30,110,170,0.9)');
    c.fillStyle = g;
    c.fillRect(tx + 1, ty + 2, tw - 2, th - 3);
    rect(c, '#e6cd92', tx + 1, ty + th - 3, tw - 2, 2);
    for (let i = 0; i < 3; i++) {
      const px = tx + 4 + i * 9, sway = Math.sin(t * 1.5 + i) * 1.2;
      c.strokeStyle = '#3fae5a';
      c.lineWidth = 1.2;
      c.beginPath();
      c.moveTo(px, ty + th - 2);
      c.quadraticCurveTo(px + sway, ty + th - 8, px + sway * 1.5, ty + th - 12);
      c.stroke();
    }
    for (let i = 0; i < 4; i++) {
      const k = (t * 0.25 + i * 0.27) % 1;
      const fx = tx + 3 + ((i % 2 ? k : 1 - k) * (tw - 7)), fy = ty + 5 + i * 2.6 + Math.sin(t * 3 + i) * 0.6;
      ellipse(c, i % 2 ? '#ff5a3a' : '#5ff0ff', fx, fy, 1.6, 0.9);
    }
    ellipse(c, '#4a8a3a', tx + tw - 7, ty + th - 4, 2.6, 1.6);
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.7 + i / 3) % 1;
      ellipse(c, 'rgba(255,255,255,0.7)', tx + 6 + i, ty + th - 3 - k * (th - 4), 0.6, 0.6);
    }
    rect(c, 'rgba(255,255,255,0.35)', tx + 2, ty + 3, 1, th - 6);
    rect(c, '#2b3440', tx - 1, ty - 1, tw + 2, 2);
  },
  boComics(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 1, 3);
    block(c, x, y - 20, w, r.h + 20, '#8a5530', '#a36a40', '#6b3f22');
    const cols = ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278', '#ff9fc0', '#9b5fc0'];
    for (let row = 0; row < 3; row++) {
      rect(c, '#5a3420', x + 1, y - 19 + row * 8 + 7, w - 2, 1);
      for (let i = 0; i < 6; i++) rect(c, cols[(i + row * 2) % cols.length], x + 2 + i * ((w - 4) / 6), y - 18 + row * 8, (w - 4) / 6 - 0.6, 6);
    }
  },
  boLego(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 5, w, 5, '#f4f1ea', '#ffffff', '#cfc8ba');
    rect(c, '#cfc8ba', x + 2, y, 1.5, r.h);
    rect(c, '#cfc8ba', x + w - 3.5, y, 1.5, r.h);
    // A colourful half-built space station.
    block(c, x + 5, y - 11, 8, 6, '#3a8fd9');
    block(c, x + 9, y - 16, 4, 5, '#ffd34e');
    block(c, x + 15, y - 9, 6, 4, '#e2493b');
    block(c, x + 17, y - 14, 3, 5, '#57c278');
    for (const [bx, col] of [[4, '#e2493b'], [24, '#ffd34e']] as const) block(c, x + bx, y - 7, 3, 2, col);
  },

  // --- Night market stalls ----------------------------------------------------------
  mkTea: (c, r, t) =>
    stall(c, r, t, '珍珠奶茶', '#e8735a', (c, r) => {
      for (let i = 0; i < 5; i++) {
        const cx = r.x + 6 + i * 11;
        block(c, cx - 2.5, r.y - 17, 5, 8, 'rgba(240,225,200,0.95)');
        rect(c, '#c49a6c', cx - 2, r.y - 13, 4, 3.5);
        for (let k = 0; k < 3; k++) ellipse(c, '#2a1810', cx - 1.5 + k * 1.5, r.y - 10.4, 0.6, 0.6);
        rect(c, i % 2 ? '#3a8fd9' : '#e2493b', cx + 0.5, r.y - 21, 0.8, 5);
      }
    }),
  mkChicken: (c, r, t) =>
    stall(c, r, t, '炸雞排', '#d9a020', (c, r, t) => {
      block(c, r.x + 4, r.y - 15, 22, 6, '#3a3a40', '#5a5a64');
      rect(c, '#ffcf5a', r.x + 5, r.y - 14, 20, 2);
      steam(c, r.x + 15, r.y - 16, t);
      for (let i = 0; i < 3; i++) block(c, r.x + 32 + i * 8, r.y - 14, 7, 5, '#c8862a', '#e0a040');
    }),
  mkBalls: (c, r, t) =>
    stall(c, r, t, '地瓜球', '#e2a03a', (c, r, t) => {
      block(c, r.x + 6, r.y - 16, 24, 7, '#3a3a40', '#5a5a64');
      for (let i = 0; i < 9; i++) ellipse(c, '#f2b04a', r.x + 9 + (i % 5) * 4.5, r.y - 13 - Math.floor(i / 5) * 2.5 - Math.abs(Math.sin(t * 4 + i)) * 0.8, 1.8, 1.8);
      for (let i = 0; i < 3; i++) block(c, r.x + 36 + i * 7, r.y - 16, 6, 7, '#f4f1ea', '#ffffff');
    }),
  mkTofu: (c, r, t) =>
    stall(c, r, t, '臭豆腐', '#57a06a', (c, r, t) => {
      block(c, r.x + 5, r.y - 15, 22, 6, '#3a3a40', '#5a5a64');
      for (let i = 0; i < 4; i++) block(c, r.x + 7 + i * 5, r.y - 14, 4, 3, '#d9a050', '#ecc070');
      steam(c, r.x + 16, r.y - 16, t);
      // A little cartoon "smell" wave, for fun.
      c.strokeStyle = 'rgba(160,200,80,0.6)';
      c.lineWidth = 0.8;
      c.beginPath();
      for (let i = 0; i < 12; i++) c.lineTo(r.x + 34 + i, r.y - 20 - Math.sin(t * 3 + i * 0.8) * 1.5 - i * 0.6);
      c.stroke();
      block(c, r.x + 34, r.y - 14, 18, 5, '#f4f1ea');
      rect(c, '#e6a040', r.x + 36, r.y - 13, 14, 2);
    }),
  mkOyster: (c, r, t) =>
    stall(c, r, t, '蚵仔煎', '#3a8fd9', (c, r, t) => {
      ellipse(c, '#2b2b30', r.x + 16, r.y - 12, 12, 4);
      ellipse(c, '#f2d38a', r.x + 12, r.y - 12.5, 4.5, 2);
      ellipse(c, '#f2d38a', r.x + 21, r.y - 12, 4.5, 2);
      rect(c, '#e2493b', r.x + 11, r.y - 13, 3, 0.8);
      rect(c, '#e2493b', r.x + 20, r.y - 12.5, 3, 0.8);
      steam(c, r.x + 16, r.y - 15, t);
      for (let i = 0; i < 3; i++) ellipse(c, '#b8b2a6', r.x + 38 + i * 6, r.y - 12, 2.5, 1.5);
    }),
  mkIce: (c, r, t) =>
    stall(c, r, t, '芒果冰', '#ff9fc0', (c, r) => {
      for (let i = 0; i < 4; i++) {
        const cx = r.x + 9 + i * 13;
        block(c, cx - 5, r.y - 12, 10, 3, '#f4f1ea', '#ffffff');
        ellipse(c, '#f8f8ff', cx, r.y - 13, 4.5, 3);
        for (let k = 0; k < 4; k++) rect(c, '#ffb43a', cx - 3 + (k % 2) * 3, r.y - 16 + Math.floor(k / 2) * 2, 2.2, 2);
        rect(c, 'rgba(255,250,230,0.8)', cx - 3, r.y - 17.5, 6, 0.8);
      }
    }),
  mkFish: (c, r, t) =>
    stall(c, r, t, '撈金魚', '#3a8fd9', (c, r, t) => {
      // Shallow basin with circling goldfish.
      const cx = r.x + r.w / 2, cy = r.y - 13;
      ellipse(c, '#2a5f8a', cx, cy, r.w / 2 - 3, 5);
      ellipse(c, '#5fc0e8', cx, cy - 0.5, r.w / 2 - 4.5, 4);
      for (let i = 0; i < 6; i++) {
        const a = t * (0.6 + i * 0.07) + i;
        ellipse(c, i === 0 ? '#ffd34e' : '#ff7a3a', cx + Math.cos(a) * (r.w / 2 - 9), cy + Math.sin(a) * 2.2, 1.4, 0.8);
      }
    }),
  mkRings: (c, r, t) =>
    stall(c, r, t, '套圈圈', '#9b5fc0', (c, r, t) => {
      // A slanted board of little prizes and bottles.
      block(c, r.x + 3, r.y - 20, r.w - 6, 12, '#f2e6cf', '#fff6e6');
      const cols = ['#57c278', '#e2493b', '#3a8fd9', '#ffd34e', '#ff9fc0'];
      for (let i = 0; i < 10; i++) {
        const px = r.x + 6 + (i % 5) * 10, py = r.y - 17 + Math.floor(i / 5) * 6;
        block(c, px, py, 3, 5, cols[i % cols.length]);
      }
      const k = (t * 0.5) % 1;
      c.strokeStyle = '#ff5a3a';
      c.lineWidth = 1;
      c.beginPath();
      c.ellipse(r.x + 10 + k * 40, r.y - 24 - Math.sin(k * Math.PI) * 6, 3, 1.4, 0, 0, Math.PI * 2);
      c.stroke();
    }),
  mkClaw(c, r, t) {
    const { x, y, w, h } = r;
    shadow(c, x + w / 2, y + h, w / 2 + 2, 4, 0.35);
    // A row of three glowing claw machines.
    for (let i = 0; i < 3; i++) {
      const mx = x + i * (w / 3) + 1, mw = w / 3 - 2;
      const glow = ['#ff5aa0', '#5ad0ff', '#ffd34e'][i];
      block(c, mx, y - 42, mw, 44 + h - 2, '#2b2f3a', '#3a4050');
      rect(c, glow, mx + 1, y - 41, mw - 2, 4);
      // Glass box with plushies.
      const gx = mx + 2, gy = y - 36, gw = mw - 4, gh = 20;
      c.fillStyle = 'rgba(160,220,255,0.35)';
      c.fillRect(gx, gy, gw, gh);
      for (let k = 0; k < 5; k++) {
        const col = ['#ffb0c8', '#ffe08a', '#b0e8ff', '#c8f0a0', '#e0c0ff'][(k + i) % 5];
        ellipse(c, col, gx + 3 + k * (gw - 6) / 4, gy + gh - 3 - (k % 2) * 2.5, 2.6, 2.2);
        rect(c, OUTLINE, gx + 2.2 + k * (gw - 6) / 4, gy + gh - 3.6 - (k % 2) * 2.5, 0.6, 0.6);
      }
      // The claw sliding back and forth.
      const cx = gx + 3 + ((Math.sin(t * 0.9 + i * 2) + 1) / 2) * (gw - 6);
      rect(c, '#c8cdd2', cx - 0.3, gy, 0.6, 6);
      c.strokeStyle = '#c8cdd2';
      c.lineWidth = 0.8;
      c.beginPath();
      c.moveTo(cx - 2, gy + 9);
      c.lineTo(cx, gy + 6);
      c.lineTo(cx + 2, gy + 9);
      c.stroke();
      rect(c, 'rgba(255,255,255,0.35)', gx + 1, gy + 1, 1, gh - 2);
      block(c, mx + 3, y - 13, mw - 6, 5, '#1b1d24');
      ellipse(c, '#e2493b', mx + mw / 2, y - 10.5, 1.5, 1.2);
      rect(c, glow, mx + 1, y - 4, mw - 2, 1);
    }
  },

  // --- Festivals --------------------------------------------------------------------
  fsRiddles(c, r, t) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    rect(c, OUTLINE, x + 1, y - 34, 2, 34 + r.h);
    rect(c, OUTLINE, x + w - 3, y - 34, 2, 34 + r.h);
    rect(c, '#8a2a1e', x, y - 35, w, 2);
    for (let i = 0; i < 3; i++) {
      const lx = x + 8 + i * ((w - 16) / 2);
      lantern(c, lx, y - 33, i % 2 ? '#ffb43a' : '#e2493b', t, i);
      // Paper tag with the riddle swinging under each lantern.
      const sw = Math.sin(t * 1.6 + i) * 0.8;
      rect(c, '#fff6dc', lx - 2.5 + sw, y - 20, 5, 7);
      rect(c, '#c0392b', lx - 1.5 + sw, y - 18.5, 3, 0.6);
      rect(c, '#c0392b', lx - 1.5 + sw, y - 16.5, 3, 0.6);
    }
  },
  fsTangyuan(c, r, t) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 3, 3);
    block(c, x, y - 12, w, 12 + r.h, '#c0392b', '#e05a48', '#8a2a1e');
    ellipse(c, '#1b1d24', x + 4, y + r.h, 2.5, 2.5);
    ellipse(c, '#1b1d24', x + w - 4, y + r.h, 2.5, 2.5);
    block(c, x + 6, y - 20, w - 12, 8, '#9aa0a6', '#c8cdd2');
    for (let i = 0; i < 4; i++) ellipse(c, i % 2 ? '#ffb0c8' : '#fbf8ef', x + 10 + i * 4, y - 20, 1.6, 1.4);
    steam(c, x + w / 2, y - 22, t);
  },
  fsBoat(c, r, t) {
    // Two dragon boats bobbing on the pond, just north of the shore.
    for (let i = 0; i < 2; i++) {
      const bx = r.x - 6 + i * 34, by = r.y - 14 - i * 16 + Math.sin(t * 2 + i) * 0.8;
      const col = i ? '#2e9d6a' : '#c0392b';
      ellipse(c, 'rgba(255,255,255,0.35)', bx + 18, by + 3, 22, 2);
      block(c, bx, by - 4, 36, 5, col, shade(col, 0.25), shade(col, -0.3));
      for (let k = 0; k < 6; k++) rect(c, '#ffd34e', bx + 4 + k * 5, by - 3, 2, 1);
      // Dragon head and tail.
      block(c, bx + 34, by - 10, 6, 7, col);
      ellipse(c, '#ffd34e', bx + 38, by - 8, 1.2, 1.2);
      rect(c, '#ffd34e', bx + 39, by - 12, 3, 1.5);
      block(c, bx - 4, by - 8, 5, 5, col);
      for (let k = 0; k < 5; k++) {
        const pa = Math.sin(t * 6 + k + i * 2) * 1.5;
        ellipse(c, '#3a2418', bx + 6 + k * 6, by - 6, 1.4, 1.4);
        rect(c, '#8a5530', bx + 6 + k * 6 + pa, by - 4, 0.8, 5);
      }
      ellipse(c, '#e2493b', bx + 30, by - 8, 2.4, 2.4);
    }
  },
  fsZongzi(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 6, w, 6 + r.h, '#8a5530', '#a36a40');
    for (let i = 0; i < 5; i++) {
      const zx = x + 4 + (i % 3) * 8, zy = y - 9 - Math.floor(i / 3) * 4;
      c.fillStyle = '#4a8a3a';
      c.beginPath();
      c.moveTo(zx, zy + 4);
      c.lineTo(zx + 3, zy - 2);
      c.lineTo(zx + 6, zy + 4);
      c.closePath();
      c.fill();
      rect(c, '#e8d8b8', zx + 1, zy + 1, 4, 0.6);
    }
    for (let i = 0; i < 3; i++) block(c, x + w - 9, y - 16 + i * 2, 7, 2, '#5aa04a');
  },
  fsEgg(c, r, t) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    block(c, x, y - 6, w, 6 + r.h, '#f4f1ea', '#ffffff', '#cfc8ba');
    for (let i = 0; i < 4; i++) {
      const wob = i === 1 ? Math.sin(t * 5) * 0.4 : 0;
      ellipse(c, '#f8f0e0', x + 5 + i * 6 + wob, y - 9, 1.8, 2.6);
      rect(c, 'rgba(0,0,0,0.12)', x + 4 + i * 6, y - 7, 3, 0.5);
    }
  },
  fsBBQ(c, r, t) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    rect(c, '#2b2b30', x + 4, y - 6, 1.2, 6 + r.h);
    rect(c, '#2b2b30', x + w - 5, y - 6, 1.2, 6 + r.h);
    block(c, x + 1, y - 12, w - 2, 7, '#2b2b30', '#4a4a54');
    for (let i = 0; i < 8; i++) ellipse(c, `rgba(255,${120 + (i % 3) * 30},40,${0.6 + Math.sin(t * 8 + i) * 0.3})`, x + 5 + i * 5, y - 9, 1.5, 1);
    for (let i = 0; i < 3; i++) block(c, x + 6 + i * 11, y - 14, 8, 2.5, i === 1 ? '#c0392b' : '#f2c94c');
    for (let i = 0; i < 4; i++) {
      const k = (t * 0.4 + i / 4) % 1;
      ellipse(c, `rgba(200,200,200,${0.4 * (1 - k)})`, x + w / 2 + Math.sin(t + i * 2) * 4, y - 16 - k * 24, 2 + k * 5, 1.5 + k * 3);
    }
  },
  fsPomelo(c, r) {
    const { x, y, w } = r;
    shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
    ellipse(c, '#a0703a', x + w / 2, y - 3, w / 2, 5);
    for (let i = 0; i < 5; i++) ellipse(c, '#c8d84a', x + 6 + (i % 3) * 8, y - 7 - Math.floor(i / 3) * 4, 4, 3.5);
    rect(c, '#4a8a3a', x + 13, y - 15, 2, 2);
  },
  fsLion(c, r, t) {
    // A golden lion (two dancers under the costume) hopping to the drum.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    const hop = Math.abs(Math.sin(t * 4)) * 5;
    shadow(c, cx, by, 16, 3);
    block(c, cx - 14, by - 14 - hop * 0.4, 26, 10, '#ffd34e', '#fff0a0', '#c8901a');
    for (let i = 0; i < 6; i++) rect(c, '#e2493b', cx - 12 + i * 4, by - 6 - hop * 0.4, 2, 3);
    for (const dx of [-10, -4, 4, 10]) rect(c, '#2a2f3d', cx + dx, by - 5, 2, 5);
    const hx = cx + 14, hy = by - 22 - hop;
    block(c, hx - 8, hy - 6, 16, 14, '#e2493b', '#ff7a5a', '#a8261c');
    ellipse(c, '#ffffff', hx - 3, hy - 1, 2.5, 2.2);
    ellipse(c, '#ffffff', hx + 3, hy - 1, 2.5, 2.2);
    const blink = Math.sin(t * 3) > 0.9 ? 0.3 : 1.4;
    ellipse(c, '#1b1d24', hx - 3, hy - 1, 1.2, blink);
    ellipse(c, '#1b1d24', hx + 3, hy - 1, 1.2, blink);
    rect(c, '#ffd34e', hx - 7, hy - 8, 14, 2);
    ellipse(c, '#ffffff', hx, hy + 6, 5, 2);
    for (let i = 0; i < 5; i++) rect(c, '#fff6dc', hx - 9 + i * 4, hy - 10 - Math.sin(t * 6 + i) * 1.2, 1.5, 3);
    // Drummer at the side.
    ellipse(c, '#c0392b', r.x - 4, by - 6, 5, 4);
    rect(c, '#ffd34e', r.x - 9, by - 7, 10, 1);
    rect(c, '#8a5530', r.x - 6 + Math.sin(t * 8) * 2, by - 13, 0.8, 5);
  },
  fsReunion(c, r, t) {
    // A bubbling hot pot and dishes on the dining table.
    const cx = r.x + r.w / 2, ty = r.y - 38;
    ellipse(c, '#9aa0a6', cx, ty, 8, 3);
    ellipse(c, '#d9663a', cx, ty - 0.5, 6.5, 2.2);
    for (let i = 0; i < 4; i++) ellipse(c, 'rgba(255,240,200,0.8)', cx - 4 + i * 2.6, ty - 0.5 + Math.sin(t * 6 + i) * 0.4, 0.8, 0.6);
    steam(c, cx, ty - 2, t);
    ellipse(c, '#f4f1ea', cx - 16, ty + 2, 4, 1.6);
    ellipse(c, '#e8a040', cx - 16, ty + 1.5, 3, 1);
    ellipse(c, '#f4f1ea', cx + 16, ty + 2, 4, 1.6);
    ellipse(c, '#fbf8ef', cx + 16, ty + 1.2, 2.5, 1);
  },
};

/** Things that change every frame (never cached). */
export const ANIMATED3_OBJ = ['grCooker', 'boConsole', 'boAquarium', 'mkTea', 'mkChicken', 'mkBalls', 'mkTofu', 'mkOyster', 'mkIce', 'mkFish', 'mkRings', 'mkClaw'];
export const ANIMATED3_DECOR = ['fanStand', 'lanternString', 'marketGate', 'laundryRack'];
/** Decor drawn above characters (it hangs overhead). */
export const OVERHEAD_DECOR = new Set<string>(['lanternString', 'marketGate']);

function lantern(c: Ctx, x: number, y: number, col: string, t: number, k: number): void {
  const sw = Math.sin(t * 1.6 + k) * 0.8;
  ellipse(c, `rgba(255,150,80,0.22)`, x + sw, y + 4, 7, 6);
  rect(c, '#2a1810', x - 0.4 + sw, y - 2, 0.8, 2);
  block(c, x - 3 + sw, y, 6, 2, '#2a1810');
  ellipse(c, col, x + sw, y + 5, 4, 3.6);
  rect(c, 'rgba(0,0,0,0.18)', x - 3.5 + sw, y + 5, 7, 0.6);
  ellipse(c, 'rgba(255,240,180,0.55)', x - 1 + sw, y + 4, 1.2, 1.8);
  block(c, x - 2.5 + sw, y + 8.2, 5, 1.6, '#2a1810');
  rect(c, '#ffd34e', x - 0.3 + sw, y + 9.8, 0.6, 2.2);
}

export function drawDecor3(c: Ctx, d: Decor, t: number): boolean {
  const { x, y } = d;
  switch (d.kind) {
    case 'kitchenCounter': {
      const w = d.w ?? 48;
      shadow(c, x, y, w / 2 + 2, 3);
      block(c, x - w / 2, y - 14, w, 14, '#e6e0d2', '#f6f2e8', '#bdb6a6');
      rect(c, '#9a948a', x - w / 2 + 1, y - 15, w - 2, 2);
      ellipse(c, '#c8cdd2', x + w / 2 - 8, y - 13, 4, 1.5);
      rect(c, '#9aa0a6', x + w / 2 - 8, y - 17, 0.8, 3);
      for (let i = 0; i < 2; i++) rect(c, 'rgba(0,0,0,0.18)', x - w / 2 + 4 + i * (w / 2), y - 10, w / 2 - 8, 0.6);
      return true;
    }
    case 'altar': {
      const w = d.w ?? 48;
      shadow(c, x, y, w / 2 + 2, 3);
      // Red-lacquered family table with ancestor tablets, fruit and two red lamps.
      block(c, x - w / 2, y - 16, w, 16, '#8a2a1e', '#b0402e', '#5a1a12');
      rect(c, '#d9b450', x - w / 2 + 2, y - 15, w - 4, 1);
      rect(c, '#d9b450', x - w / 2 + 2, y - 3, w - 4, 0.8);
      block(c, x - 5, y - 28, 10, 12, '#5a1a12', '#7a2a1e');
      rect(c, '#d9b450', x - 3, y - 26, 6, 8);
      for (const dx of [-14, 14]) {
        block(c, x + dx - 2.5, y - 26, 5, 9, '#e2493b', '#ff7a5a');
        ellipse(c, 'rgba(255,90,60,0.3)', x + dx, y - 22, 6, 6);
      }
      for (let i = 0; i < 3; i++) ellipse(c, '#ff9a2a', x - 9 + i * 3, y - 18, 1.6, 1.5);
      ellipse(c, '#ffd34e', x + 9, y - 18, 2.2, 1.8);
      return true;
    }
    case 'woodChair': {
      shadow(c, x, y, 6, 2);
      block(c, x - 5, y - 8, 10, 3, '#6e3f22', '#8a5530');
      block(c, x + (d.variant ? 3 : -5), y - 18, 2, 12, '#6e3f22', '#8a5530');
      rect(c, '#4a2a16', x - 4, y - 5, 1.2, 5);
      rect(c, '#4a2a16', x + 3, y - 5, 1.2, 5);
      return true;
    }
    case 'fanStand': {
      shadow(c, x, y, 5, 1.6);
      rect(c, '#9aa0a6', x - 0.6, y - 18, 1.2, 18);
      ellipse(c, '#3a8fd9', x, y - 1, 4, 1.3);
      c.strokeStyle = 'rgba(220,230,240,0.9)';
      c.lineWidth = 0.5;
      c.beginPath();
      c.arc(x, y - 21, 6, 0, Math.PI * 2);
      c.stroke();
      for (let i = 0; i < 3; i++) {
        const a = t * 18 + (i * Math.PI * 2) / 3;
        ellipse(c, 'rgba(90,170,230,0.75)', x + Math.cos(a) * 3, y - 21 + Math.sin(a) * 3, 2.4, 1.4);
      }
      ellipse(c, '#3a8fd9', x, y - 21, 1.4, 1.4);
      return true;
    }
    case 'catBasket': {
      shadow(c, x, y, 8, 2);
      ellipse(c, '#a0703a', x, y - 3, 8, 4);
      ellipse(c, '#e8d8b8', x, y - 4, 6.5, 2.8);
      ellipse(c, '#fbf8ef', x - 1, y - 5, 4, 2.4);
      ellipse(c, '#fbf8ef', x + 3, y - 6, 2, 1.8);
      rect(c, '#2a1810', x + 3, y - 6.2, 1.4, 0.4);
      return true;
    }
    case 'bunkBed': {
      shadow(c, x, y, 10, 3);
      block(c, x - 8, y - 36, 16, 36, '#7a5034', '#9a6a44', '#55361f');
      for (const by of [y - 34, y - 14]) {
        block(c, x - 7, by, 14, 10, '#3a8fd9', '#5aa8e8');
        rect(c, '#fbf8ef', x - 6, by + 1, 5, 3);
      }
      for (let i = 0; i < 4; i++) rect(c, '#55361f', x + 6, y - 24 + i * 5, 3, 1);
      return true;
    }
    case 'shoeCabinet': {
      shadow(c, x, y, 7, 2);
      block(c, x - 6, y - 10, 12, 10, '#e6e0d2', '#f6f2e8');
      rect(c, '#bdb6a6', x - 5, y - 5, 10, 0.6);
      for (const [dx, col] of [[-4, '#e2493b'], [1, '#3a8fd9']] as const) block(c, x + dx, y - 13, 4, 3, col);
      return true;
    }
    case 'laundryRack': {
      const sw = Math.sin(t * 1.3) * 0.8;
      shadow(c, x, y, 10, 2);
      rect(c, '#c8cdd2', x - 10, y - 20, 0.8, 20);
      rect(c, '#c8cdd2', x + 9, y - 20, 0.8, 20);
      rect(c, '#c8cdd2', x - 10, y - 20, 20, 0.8);
      const cols = ['#ffd34e', '#3a8fd9', '#ff9fc0'];
      for (let i = 0; i < 3; i++) block(c, x - 8 + i * 6 + sw, y - 19, 5, 7 + (i % 2) * 2, cols[i]);
      return true;
    }
    case 'lanternString': {
      // A sagging wire across the street with swinging lanterns (hangs high above the street).
      const w = d.w ?? 128, x0 = x, x1 = x + w, y0 = y - 66;
      const sag = (k: number) => y0 + Math.sin(k * Math.PI) * 10;
      c.strokeStyle = '#2a1810';
      c.lineWidth = 0.6;
      c.beginPath();
      for (let k = 0; k <= 1.0001; k += 0.05) c.lineTo(x0 + k * w, sag(k));
      c.stroke();
      const cols = ['#e2493b', '#ff7a3a', '#e2493b', '#ffb43a'];
      for (let i = 1; i < 6; i++) {
        const k = i / 6;
        lantern(c, x0 + k * w, sag(k), cols[(i + (d.variant ?? 0)) % cols.length], t, i + x0);
      }
      void x1;
      return true;
    }
    case 'stool': {
      shadow(c, x, y, 4, 1.4);
      ellipse(c, '#e2493b', x, y - 6, 4, 1.6);
      rect(c, '#b83a2e', x - 3, y - 6, 1, 6);
      rect(c, '#b83a2e', x + 2, y - 6, 1, 6);
      return true;
    }
    case 'foodTable': {
      const w = d.w ?? 48;
      shadow(c, x, y, w / 2, 3);
      block(c, x - w / 2, y - 10, w, 4, '#e6e0d2', '#ffffff');
      rect(c, '#9a948a', x - w / 2 + 3, y - 6, 1.2, 6);
      rect(c, '#9a948a', x + w / 2 - 4, y - 6, 1.2, 6);
      ellipse(c, '#f4f1ea', x - 8, y - 11, 3, 1.2);
      ellipse(c, '#d9a050', x - 8, y - 11.5, 2, 0.8);
      block(c, x + 6, y - 15, 3, 5, 'rgba(240,225,200,0.95)');
      return true;
    }
    case 'scooter': {
      const col = d.variant ? '#3a8fd9' : '#f4f1ea';
      shadow(c, x, y, 10, 2.4);
      ellipse(c, '#1b1d24', x - 7, y - 2, 2.6, 2.6);
      ellipse(c, '#1b1d24', x + 7, y - 2, 2.6, 2.6);
      block(c, x - 8, y - 9, 14, 6, col, '#ffffff');
      block(c, x + 4, y - 14, 4, 11, col, '#ffffff');
      rect(c, '#2a1810', x + 3, y - 15, 7, 1.2);
      block(c, x - 7, y - 11, 8, 2.5, '#2a2a30');
      return true;
    }
    case 'marketGate': {
      // Red arch with a glowing “夜市” sign over the west end of the street.
      const w = d.w ?? 40;
      for (const px of [x - w / 2, x + w / 2 - 3]) {
        shadow(c, px + 1.5, y, 3, 1.5);
        block(c, px, y - 44, 3, 44, '#c0392b', '#e05a48', '#8a2a1e');
      }
      block(c, x - w / 2 - 4, y - 50, w + 8, 6, '#c0392b', '#e05a48', '#8a2a1e');
      rect(c, '#ffd34e', x - w / 2 - 3, y - 45, w + 6, 0.8);
      sign(c, '夜市', x, y - 62, 24, '#2a1810', '#ffd34e');
      for (let i = 0; i < 4; i++) lantern(c, x - w / 2 + 4 + i * ((w - 8) / 3), y - 44, i % 2 ? '#ffb43a' : '#e2493b', t, i);
      return true;
    }
    case 'festLanterns': {
      // A pole with lanterns: red (Lantern Festival), round moons (Moon Festival), 福 (New Year).
      const v = d.variant ?? 0;
      const kind = v >= 20 ? 'newYear' : v >= 10 ? 'moon' : 'lantern';
      shadow(c, x, y, 3, 1.2);
      rect(c, OUTLINE, x - 0.8, y - 34, 1.6, 34);
      rect(c, OUTLINE, x - 8, y - 34, 16, 1);
      for (const dx of [-6, 6]) {
        if (kind === 'moon') {
          ellipse(c, 'rgba(255,230,140,0.25)', x + dx, y - 26, 7, 7);
          ellipse(c, '#ffe08a', x + dx, y - 26, 4, 4);
          ellipse(c, '#f2c94c', x + dx + 1.5, y - 27, 1.2, 1);
        } else lantern(c, x + dx, y - 34, kind === 'newYear' ? '#c0392b' : (v + dx) % 2 ? '#ffb43a' : '#e2493b', t, v + dx);
      }
      if (kind === 'newYear') {
        c.font = `900 4px ${CJK}`;
        c.textAlign = 'center';
        c.fillStyle = '#ffd34e';
        for (const dx of [-6, 6]) c.fillText('福', x + dx, y - 28);
      }
      return true;
    }
    case 'festCouplet': {
      // The 福 poster hung upside down: “福倒了” sounds like “luck has arrived”.
      const cx = x, cy = y - 30;
      c.save();
      c.translate(cx, cy);
      c.rotate(Math.PI / 4);
      block(c, -5, -5, 10, 10, '#c0392b', '#e05a48');
      c.restore();
      c.save();
      c.translate(cx, cy);
      c.rotate(Math.PI);
      c.font = `900 7px ${CJK}`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillStyle = '#ffd34e';
      c.fillText('福', 0, 0.5);
      c.restore();
      return true;
    }
    default:
      return false;
  }
}
